import React, { useState, useEffect, useRef } from "react";
import InputTextfield from "../atoms/InputTextField";
import {
  Fields,
  TimeStates,
  Weekdays,
  calculateTotalTime,
  calculateWeekTotal,
  convertTimeToPercentage,
  defaultFields,
  defaultWeekdays,
  fieldLabels,
  formatTime,
  formatTotalValue,
  isEqual,
  isValidTime,
  defaultWeekDaysLabels,
  ErrorStates,
} from "../utlis/PlanningUtility";
import { t } from "components/CentralDataMangement/translation/Translation";
import { InputRef, useTimeTableNavigation } from "./TimeTableNavigation";

interface WeeklyPlanningProps {
  weekdays?: Weekdays[];
  fields?: Fields[];
  weekDaysLabels?: Record<Weekdays, string>;
  disabledDays?: Weekdays[]; // Add this to specify days where fields should be disabled
  quickEntryDays?: Weekdays[];
  onChange?: (timeStates: TimeStates) => void;
  value: TimeStates; // Make value mandatory
  dates?: { weekday: Weekdays; date: string }[]
  isValidateError?: boolean;
  isViewMode?: boolean;
  errors?: ErrorStates;
}

const TimeTable: React.FC<WeeklyPlanningProps> = ({
  weekdays = defaultWeekdays,
  weekDaysLabels = defaultWeekDaysLabels,
  disabledDays = [],
  quickEntryDays = [1, 2, 3, 4, 5],
  fields = defaultFields,
  onChange,
  value,
  dates,
  isValidateError,
  errors,
  isViewMode = false,
}) => {
  const [quickEntries, setQuickEntries] = useState<Record<Fields, string>>(
    fields.reduce(
      (acc, field) => ({ ...acc, [field]: "" }),
      {} as Record<Fields, string>
    )
  );

  const [timeStates, setTimeStates] = useState<TimeStates>(value);

  const prevValueRef = useRef<TimeStates>(value);

  useEffect(() => {
    // Update state only if value has changed
    if (!isEqual(prevValueRef.current, value)) {
      setTimeStates(value);
      prevValueRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    const updatedNumberTimes = weekdays.reduce((acc, day) => {
      const { from, to, breakFrom, breakUntil } = timeStates[day] || {};
      const date = dates?.find((d) => d.weekday === day)?.date || "";

      // Check if the day is disabled
      if (disabledDays.includes(day)) {
        // Set total to '0,00' for disabled days and other fields to empty string
        acc[day] = {
          ...timeStates[day],
          from: "",
          to: "",
          breakFrom: "",
          breakUntil: "",
          total: "", // Ensure the total is always '0,00' for disabled days
          date
        };
      } else {
        // Perform normal calculation for enabled days
        const totalTime = calculateTotalTime(from, to, breakFrom, breakUntil);
        const updatedTotal = convertTimeToPercentage(totalTime);
        acc[day] = {
          ...timeStates[day],
          total: updatedTotal,
          date
        };
      }

      return acc;
    }, {} as Record<Weekdays, Record<Fields, string>>);

    // Update state if there are changes in total time
    if (!isEqual(updatedNumberTimes, timeStates)) {
      if (onChange) {
        onChange(updatedNumberTimes);
      }
    }

  }, [weekdays, timeStates, disabledDays, calculateTotalTime, convertTimeToPercentage]);

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    day: Weekdays,
    field: Fields
  ) => {
    if (isViewMode) return;
    const value =
      field === "total"
        ? formatTotalValue(e.target.value)
        : formatTime(e.target.value);

    const updatedTimeStates = {
      ...timeStates,
      [day]: { ...timeStates[day], [field]: value },
    };

    setTimeStates(updatedTimeStates);

    if (onChange) {
      onChange(updatedTimeStates);
    }
  };

  const handleQuickEntryChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: Fields
  ) => {
    if (isViewMode) return;
    let value: string;
    if (field === "total") {
      value = formatTotalValue(e.target.value);
      applyQuickEntry(value, field);
    } else {
      value = formatTime(e.target.value);
      if (value.length === 5 && isValidTime(value)) {
        applyQuickEntry(value, field);
      } else if (value.length === 0) {
        applyQuickEntry("00:00", field);
      }
    }

    setQuickEntries((prev) => ({ ...prev, [field]: value }));
  };

  const applyQuickEntry = (value: string, field: Fields) => {
    const updatedTimeStates = weekdays
      .filter((day) => quickEntryDays.includes(day))
      .reduce(
        (acc, day) => ({
          ...acc,
          [day]: { ...timeStates[day], [field]: value },
        }),
        {} as Record<Weekdays, Record<Fields, string>>
      );

    setTimeStates((prev) => ({
      ...prev,
      ...updatedTimeStates,
    }));

    if (onChange) {
      onChange({
        ...timeStates,
        ...updatedTimeStates,
      });
    }
  };

  const getCellStyleForInvalidTime = (time: string, field: Fields, error?: string) => {
    if (field === "total") return {}; // No validation styling for total field
    if (error && error.trim() != "") return "error-border";
    return !isValidTime(time) && time ? "error-border" : "";
  };

  // Disabling the past days
  const isPastDay = (day: number) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today's date to avoid time mismatches

    const targetDate = dates?.find((d) => d.weekday === day)?.date;

    if (!targetDate) return false; // Skip if no matching date is found
    // Parse yyyy/mm/dd format to Date object
    const [yearPart, monthPart, dayPart] = targetDate.split("-").map(Number);
    const parsedDate = new Date(yearPart, monthPart - 1, dayPart); // Month is zero-indexed
    parsedDate.setHours(0, 0, 0, 0);

    return parsedDate < today; // Disable if the date is before today
  };

  const isFieldDisabled = (day: Weekdays, field: Fields) => {
    return disabledDays.includes(day) || field === "total";
  };

  const { setInputRef, handleKeyDown } = useTimeTableNavigation(fields.length, weekdays.length + 1); // +1 for quick entry

  return (
    <div className="weeklyPlanning weeklyPlanningTable">
      <table className="table table-bordered mb-0">
        <thead>
          <tr className="TableHeader">
            <th></th>
            <th>{t("Quick Entry")}</th>
            {weekdays.map((day) => (
              <th key={day}>{t(weekDaysLabels[day])}</th>
            ))}
            <th>{t("Week total")}</th>
          </tr>
        </thead>
        <tbody>
          {fields.filter(field => field != 'date').map((field, rowIndex: number) => (
            <tr key={field} className="border-bottom align-middle">
              <td className="align-middle weeklyPlanningData">
                {t(fieldLabels[field])}
              </td>
              <td className="p-0 position-relative align-middle">
                <InputTextfield
                  type="text"
                  value={quickEntries[field]}
                  handleChange={(e) => handleQuickEntryChange(e, field)}
                  isDisabled={field === "total"}
                  maxLength={field === "total" ? null : 5}
                  inputRef={(element: InputRef) => setInputRef(element, rowIndex, -1,)}
                  onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, rowIndex, -1)}
                  className={`position-absolute top-0 left-0 h-100 w-100 outline-0 text-center color-dark-purple border border-white ${getCellStyleForInvalidTime(
                    quickEntries[field],
                    field
                  )}`}
                />
              </td>
              {weekdays.map((day, colIndex) => (
                <td key={day} className="p-0 position-relative align-middle">
                  <InputTextfield
                    type="text"
                    value={timeStates[day]?.[field] ?? ""}
                    handleChange={(e) => handleTimeChange(e, day, field)}
                    isDisabled={isFieldDisabled(day, field)} // Check if field is disabled
                    maxLength={field === "total" ? null : 5}
                    inputRef={(element) => setInputRef(element, rowIndex, colIndex)}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                    className={`position-absolute top-0 left-0 h-100 w-100 outline-0 text-center color-dark-purple border border-white ${getCellStyleForInvalidTime(
                      timeStates[day]?.[field] ?? "",
                      field,
                      errors?.[day]?.[field]
                    )}`}
                  />
                </td>

              ))}

              {rowIndex === 0 && (
                <td className="text-center disabled" rowSpan={fields.length}>
                  {calculateWeekTotal(timeStates)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TimeTable;

/** HOW TO USE IN CONTEXT EX:IN VACANCY
 
 ####  TYPE DECLARATION IN INTERFACE
 *
  import { TimeStates } from "components/common/utlis/PlanningUtility";
    export interface GeneralProps {
    planning:TimeStates;
    company: Option | null;
    }
 * 

####  INTILA STATE
import { initialTimeStates } from "components/common/utlis/PlanningUtility";
 general: {
        plan:initialTimeStates,
        company: null,
        location: null,
 }


 const handleWeeklyPlanningChange =
    (timeStates: TimeStates) => {
      dispatch({
        type: UPDATE_FIELDS,
        template: GENERAL,
        field: "plan",
        value: timeStates,
      });
    }

 <TimeTable
    onChange={handleWeeklyPlanningChange}
    value={state.general.plan}
/>

IF IT ASKS FOR TYPE like string | null | number use  $$$$$ string | number | null | TimeStates $$$


 */
